import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Target, Eye, Users, Award, Globe, Zap,
  ArrowRight, CheckCircle, MapPin
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { PageSEO, SEO_CONFIG, BreadcrumbSchema } from '../lib/seo';
import { siteConfig } from '../config/siteConfig';

const BRAND = { name: siteConfig.company.name };

const AboutPage = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const team = siteConfig.team;

  const stats = [
    { value: siteConfig.stats.projectsDelivered, label: 'Projects Delivered' },
    { value: siteConfig.stats.enterpriseClients, label: 'Enterprise Clients' },
    { value: siteConfig.stats.clientRetention, label: 'Client Retention' },
    { value: siteConfig.stats.teamMembers, label: 'Team Members' },
  ].filter((s) => s.value !== null && s.value !== undefined);

  const values = [
    {
      icon: Zap,
      title: 'Innovation First',
      description: 'We embrace cutting-edge technologies to deliver forward-thinking software solutions.'
    },
    {
      icon: Users,
      title: 'Client Partnership',
      description: 'We build lasting relationships based on trust, transparency, and measurable results.'
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'We maintain the highest standards in software development and IT consulting.'
    },
    {
      icon: Globe,
      title: 'Global Mindset',
      description: 'We think globally while understanding local business needs across USA, UK, and India.'
    }
  ];

  return (
    <div data-testid="about-page">
      <PageSEO 
        title={SEO_CONFIG.about.title}
        description={SEO_CONFIG.about.description}
        keywords={SEO_CONFIG.about.keywords}
        canonical="/about"
      />
      <BreadcrumbSchema items={[
        { name: 'Home', url: '/' },
        { name: 'About Us', url: '/about' }
      ]} />

      {/* Hero */}
      <section className="bg-gradient-to-br from-warm-50 to-white py-24" aria-labelledby="about-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="inline-block text-primary font-medium mb-4">About {BRAND.name}</span>
            <h1 id="about-heading" className="font-heading font-bold text-4xl sm:text-5xl text-text-primary mb-6">
              {siteConfig.company.tagline}
            </h1>
            <p className="text-lg text-text-secondary max-w-3xl mx-auto">
              {siteConfig.company.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats (hidden entirely if all stats are null) */}
      {stats.length > 0 && (
      <section className="py-16 bg-primary" aria-label="Company statistics">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className={`grid grid-cols-2 gap-8 ${
            stats.length === 1 ? 'md:grid-cols-1' :
            stats.length === 2 ? 'md:grid-cols-2' :
            stats.length === 3 ? 'md:grid-cols-3' :
            'md:grid-cols-4'
          }`}>
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <p className="text-4xl md:text-5xl font-heading font-bold text-white mb-2">
                  {stat.value}
                </p>
                <p className="text-white/80">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Mission & Vision */}
      <section className="py-24 bg-white" aria-labelledby="mission-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <motion.article
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-slate-50 rounded-3xl p-8 md:p-12"
            >
              <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
                <Target className="h-8 w-8 text-primary" aria-hidden="true" />
              </div>
              <h2 id="mission-heading" className="font-heading font-bold text-2xl text-text-primary mb-4">
                Our Mission
              </h2>
              <p className="text-text-secondary text-lg leading-relaxed">
                {siteConfig.mission}
              </p>
            </motion.article>

            <motion.article
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-primary rounded-3xl p-8 md:p-12"
            >
              <div className="h-16 w-16 rounded-2xl bg-white/20 flex items-center justify-center mb-6">
                <Eye className="h-8 w-8 text-white" aria-hidden="true" />
              </div>
              <h2 className="font-heading font-bold text-2xl text-white mb-4">
                Our Vision
              </h2>
              <p className="text-white/80 text-lg leading-relaxed">
                {siteConfig.vision}
              </p>
            </motion.article>
          </div>
        </div>
      </section>

      {/* Global Presence */}
      <section className="py-24 bg-slate-50" aria-labelledby="locations-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block text-primary font-medium mb-4">
              {siteConfig.locations.length > 1 ? 'Global Presence' : 'Our Office'}
            </span>
            <h2 id="locations-heading" className="font-heading font-bold text-3xl sm:text-4xl text-text-primary mb-4">
              {siteConfig.locations.length > 1 ? 'Where We Operate' : 'Where to Find Us'}
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              {siteConfig.locations.length > 1
                ? `With offices across ${siteConfig.locations.map((l) => l.country).join(', ')}, we serve clients worldwide.`
                : `Based in ${siteConfig.locations[0].city}, ${siteConfig.locations[0].country}, serving clients worldwide.`}
            </p>
          </motion.div>

          <div className={`grid gap-8 ${siteConfig.locations.length === 1 ? 'md:grid-cols-1 max-w-md mx-auto' : siteConfig.locations.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
            {siteConfig.locations.map((loc, index) => (
              <motion.article
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100"
              >
                <div className="flex items-center space-x-3 mb-4">
                  <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <MapPin className="h-6 w-6 text-primary" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-lg text-text-primary">{loc.city}</h3>
                    <p className="text-sm text-text-muted">{loc.country}</p>
                  </div>
                </div>
                <p className="text-text-secondary text-sm">{loc.address}</p>
                {loc.description && (
                  <p className="text-text-muted text-xs mt-2">{loc.description}</p>
                )}
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-white" aria-labelledby="values-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block text-primary font-medium mb-4">Our Values</span>
            <h2 id="values-heading" className="font-heading font-bold text-3xl sm:text-4xl text-text-primary mb-4">
              What Drives Our IT Services
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.article
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-slate-50 rounded-2xl p-8 text-center"
              >
                <div className="h-14 w-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <value.icon className="h-7 w-7 text-primary" aria-hidden="true" />
                </div>
                <h3 className="font-heading font-bold text-lg text-text-primary mb-2">
                  {value.title}
                </h3>
                <p className="text-text-secondary text-sm">
                  {value.description}
                </p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Team — hidden entirely when siteConfig.team is empty */}
      {team.length > 0 && (
      <section className="py-24 bg-slate-50" aria-labelledby="team-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block text-primary font-medium mb-4">Our Team</span>
            <h2 id="team-heading" className="font-heading font-bold text-3xl sm:text-4xl text-text-primary mb-4">
              Leadership Team at {BRAND.name}
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              Expert software development and IT consulting leaders driving innovation.
            </p>
          </motion.div>

          <div className={`grid gap-8 justify-center mx-auto ${
            team.length === 1 ? 'grid-cols-1 max-w-xs' :
            team.length === 2 ? 'sm:grid-cols-2 max-w-2xl' :
            team.length === 3 ? 'sm:grid-cols-2 lg:grid-cols-3 max-w-5xl' :
            'sm:grid-cols-2 lg:grid-cols-4'
          }`}>
            {team.map((member, index) => (
              <motion.article
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group"
              >
                <div className="relative overflow-hidden rounded-2xl mb-4">
                  <img
                    src={member.image}
                    alt={`${member.name} - ${member.role} at ${BRAND.name}`}
                    className="w-full aspect-square object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                    width="300"
                    height="300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <h3 className="font-heading font-bold text-lg text-text-primary">
                  {member.name}
                </h3>
                <p className="text-primary font-medium text-sm mb-1">{member.role}</p>
                <p className="text-text-muted text-sm">{member.bio}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Why Choose Us */}
      <section className="py-24 bg-white" aria-labelledby="why-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block text-primary font-medium mb-4">Why Choose {BRAND.name}</span>
              <h2 id="why-heading" className="font-heading font-bold text-3xl sm:text-4xl text-text-primary mb-6">
                Best IT Company for Your Software Needs
              </h2>
              <p className="text-text-secondary text-lg mb-8">
                We combine technical expertise with business acumen to deliver 
                <strong> software development solutions</strong> that truly make a difference. 
                Whether you need to build an app, website, or custom software, we're your trusted partner.
              </p>
              
              <div className="space-y-4">
                {siteConfig.whyChooseUs.map((item, i) => (
                  <div key={i} className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" aria-hidden="true" />
                    <span className="text-text-secondary">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600"
                alt="Plutops software development team collaborating on IT projects"
                className="rounded-2xl shadow-xl"
                loading="lazy"
                width="600"
                height="400"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-primary" aria-labelledby="cta-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 id="cta-heading" className="font-heading font-bold text-3xl text-white mb-6">
              Ready to Build Your Software with {BRAND.name}?
            </h2>
            <p className="text-white/80 mb-8 max-w-xl mx-auto">
              Get a free consultation from our IT experts. We help businesses build custom software, 
              web apps, and mobile applications that drive growth.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" onClick={scrollToTop}>
                <Button className="bg-white text-primary hover:bg-warm-100 rounded-full px-8 py-6">
                  Get Free Consultation
                  <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
                </Button>
              </Link>
              <Link to="/careers" onClick={scrollToTop}>
                <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-6">
                  Join Our Team
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
